import { useState } from "react";
import { Link } from "react-router-dom";
import { Droplets, Loader2, MailCheck, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Always treat as success — backend deliberately doesn't reveal whether the email exists
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      // Even on 429 rate-limit we want to show neutral copy, not leak info
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-[#F3F4F1]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2.5 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#064E3B] flex items-center justify-center">
              <Droplets className="w-5 h-5 text-white" />
            </div>
            <span className="font-display text-lg font-semibold">IV Therapy Finder</span>
          </div>
          <h1 className="font-display text-3xl font-semibold text-stone-900">
            {sent ? "Check your inbox" : "Forgot your password?"}
          </h1>
          <p className="text-stone-600 mt-1 text-sm">
            {sent
              ? "If an account exists for that email, we've sent you a reset link."
              : "Enter the email you signed up with and we'll send you a link to set a new password."}
          </p>
        </div>

        {sent ? (
          <div className="bg-white rounded-2xl border border-stone-200 p-7 text-center shadow-sm" data-testid="forgot-sent-card">
            <MailCheck className="w-12 h-12 text-[#10B981] mx-auto mb-4" />
            <p className="text-sm text-stone-700 leading-relaxed">
              We've sent a password reset link to <strong>{email}</strong>. The link expires in <strong>1 hour</strong>.
            </p>
            <p className="text-xs text-stone-500 mt-3">
              Didn't receive anything? Check your spam folder, or wait 60 seconds and try again.
            </p>
            <Link to="/login" className="inline-flex items-center gap-1.5 mt-6 text-sm font-semibold text-[#064E3B] hover:underline" data-testid="back-to-login-link">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-2xl border border-stone-200 p-7 space-y-4 shadow-sm">
            <div>
              <Label className="text-xs uppercase font-semibold tracking-wider">Email</Label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="forgot-email"
              />
            </div>
            <Button type="submit" disabled={loading || !email} className="w-full bg-[#064E3B] hover:bg-[#053d2f]" data-testid="forgot-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
            </Button>
            <p className="text-center text-sm text-stone-600 pt-2">
              Remember your password? <Link to="/login" className="text-[#064E3B] font-semibold hover:underline" data-testid="back-to-login-link">Sign in →</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
